/**
 * The one fetch wrapper. Same-origin, cookie session, JSON in and out, the
 * presenter's acting-as header on every request, and one place that notices
 * a 401 so the shell can open the session-expired dialog without unmounting
 * the page beneath it.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `request failed with ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export const ACTING_AS_KEY = 'beam.actingAs';
export const UNAUTHENTICATED_EVENT = 'beam:unauthenticated';

/** Per tab, never localStorage: a pinned tab is one persona (docs/ux/shell.md). */
export function readActingAs(): string | null {
  try {
    return window.sessionStorage.getItem(ACTING_AS_KEY);
  } catch {
    return null;
  }
}

export function writeActingAs(value: string | null): void {
  try {
    if (value) window.sessionStorage.setItem(ACTING_AS_KEY, value);
    else window.sessionStorage.removeItem(ACTING_AS_KEY);
  } catch {
    // Storage may be unavailable (private mode); acting-as is then simply off.
  }
}

export type ApiOptions = RequestInit & {
  /** The session probe handles its own 401; everything else announces it. */
  silent401?: boolean;
  json?: unknown;
};

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { silent401, json, headers, ...init } = options;
  const h = new Headers(headers);
  h.set('accept', 'application/json');
  if (json !== undefined) h.set('content-type', 'application/json');
  const actingAs = readActingAs();
  if (actingAs) h.set('x-beam-acting-as', actingAs);

  const res = await fetch(path, { ...init, headers: h, credentials: 'same-origin', body: json !== undefined ? JSON.stringify(json) : init.body });
  if (res.status === 401 && !silent401) window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    throw new ApiError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** The reason a refused write carries, when the server gave one in words a screen can show. */
export function reasonOf(err: unknown): string | null {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'reason' in err.body) {
    const r = (err.body as { reason?: unknown }).reason;
    return typeof r === 'string' ? r : null;
  }
  return null;
}
